import React, { useCallback } from 'react';
import sharedStyles from '../ModalWindows.scss';
import { useLocalization, useSelectedOrdersRows } from '@/Hooks';
import { BUTTON_THEMES } from '@/Components/Controls/Button/ButtonThemes';
import { Button } from '@/Components/Controls/Button/Button';
import { Modal } from '@/Components/Modal/Modal';
import { ModalProps } from '@/Types';
import { FormDateInput } from '@/Components/Controls/DateInput/FormDateInput';
import { FormProvider } from 'react-hook-form';
import styles from './ChangePaymentStatusModal.scss';
import {
  ChangeOrderPaymentStatusByMarkerRequest,
  ChangeOrderPaymentStatusPartiallyByMarkerRequest,
  ChangeOrderPaymentStatusToPaidForSingleOrderRequest,
} from '@/Api/MpOrdersMarking/Requests';
import { useAllPaidForm } from './hooks/useAllPaidForm';
import { useAllNotPaidForm } from './hooks/useAllNotPaidForm';
import { usePaidAndNotPaidForm } from './hooks/usePaidAndNotPaidForm';
import { usePaidAndNotPaidFormOptions } from './hooks/usePaidAndNotPaidFormOptions';
import { FormSelectInput } from '@/Components/Controls/SelectInput/FormSelectInput';
import { Label } from '@/Components/Controls/Label/Label';
import { FormCheckbox } from '@/Components/Controls/Checkbox/FormCheckbox';
import { useSingleNotPaidForm } from './hooks/useSingleNotPaidForm';
import { NumberFormInput } from '@/Components/Controls/Input/NumberFormInput';
import { orderUtils } from '@/Utils/models/Order';
import { ButtonsWrapper } from '@/Components/Forms/ButtonsSection/ButtonsSection';

type Props = ModalProps;

type BodyProps = {
  onClose: () => void;
  orderIds: number[];
  isSalesPage: boolean;
};

export function ChangePaymentStatusModal({ closeModalWindow }: Props) {
  const {
    modalWindows: { changePaymentStatus },
  } = useLocalization();

  const { orderRows, orderIds, isSalesPage } = useSelectedOrdersRows();

  const getBody = useCallback(() => {
    const isSingleAndNotPaid = orderRows.length === 1 && !orderUtils.isOrderPayed(orderRows[0]);
    const isAllPayed = orderRows.every((o) => orderUtils.isOrderPayed(o));
    const isAllNotPayed = orderRows.every((o) => !orderUtils.isOrderPayed(o));

    if (isSingleAndNotPaid) {
      return (
        <SingleNotPaidBody
          onClose={closeModalWindow}
          orderIds={orderIds}
          isSalesPage={isSalesPage}
          openAmount={orderRows[0].openAmount}
        />
      );
    } else if (isAllPayed) {
      return <AllPayedBody onClose={closeModalWindow} orderIds={orderIds} isSalesPage={isSalesPage} />;
    } else if (isAllNotPayed) {
      return <AllNotPayedBody onClose={closeModalWindow} orderIds={orderIds} isSalesPage={isSalesPage} />;
    } else {
      return <PayedAndNotPayedBody onClose={closeModalWindow} orderIds={orderIds} isSalesPage={isSalesPage} />;
    }
  }, [closeModalWindow, isSalesPage]);

  return (
    <Modal
      onClose={closeModalWindow}
      className={sharedStyles.modalWrapper}
      tourKey={'changePaymentStatusTour'}
      description={{
        title: changePaymentStatus.Title,
      }}
    >
      <div id={'changeStatus'}>{getBody()}</div>
    </Modal>
  );
}

function AllPayedBody({ onClose, orderIds, isSalesPage }: BodyProps) {
  const {
    buttons,
    modalWindows: { changePaymentStatus },
  } = useLocalization();

  const { submit } = useAllPaidForm(orderIds, isSalesPage, onClose);

  return (
    <>
      <p dangerouslySetInnerHTML={{ __html: changePaymentStatus.TextForAllPaid }} />

      <ButtonsWrapper>
        <Button theme={BUTTON_THEMES.SUCCESS_TRANSPARENT} text={buttons.Cancel} onClick={onClose} />
        <Button autoFocus={true} theme={BUTTON_THEMES.SUCCESS} text={buttons.Change} onClick={submit} />
      </ButtonsWrapper>
    </>
  );
}

function AllNotPayedBody({ onClose, orderIds, isSalesPage }: BodyProps) {
  type FormData = ChangeOrderPaymentStatusByMarkerRequest;

  const {
    buttons,
    modalWindows: { changePaymentStatus },
  } = useLocalization();

  const {
    methods,
    submit,
    rules: { required },
  } = useAllNotPaidForm(orderIds, isSalesPage, onClose);

  return (
    <FormProvider {...methods}>
      <p dangerouslySetInnerHTML={{ __html: changePaymentStatus.TextForAllNotPaid }} />

      <form className={styles.form} onSubmit={submit}>
        <Label text={changePaymentStatus.AllNotPaidForm.PaymentDate} className={styles.formLabel} />
        <FormDateInput<FormData>
          name={'paymentDate'}
          setValue={methods.setValue}
          needTimePicker={true}
          register={methods.register}
          className={styles.formControl}
          error={methods.formState.errors}
          rules={required}
        />

        <ButtonsWrapper>
          <Button theme={BUTTON_THEMES.SUCCESS_TRANSPARENT} text={buttons.Cancel} onClick={onClose} />
          <Button
            autoFocus={true}
            type={'submit'}
            theme={BUTTON_THEMES.SUCCESS}
            text={buttons.Change}
            onClick={submit}
          />
        </ButtonsWrapper>
      </form>
    </FormProvider>
  );
}

function PayedAndNotPayedBody({ onClose, orderIds, isSalesPage }: BodyProps) {
  type FormData = ChangeOrderPaymentStatusPartiallyByMarkerRequest;

  const {
    buttons,
    modalWindows: {
      changePaymentStatus: { TextForPayedAndNotPaid, PaidAndNotPaidForm },
    },
  } = useLocalization();

  const {
    methods,
    needShowDate,
    submit,
    rules: { required, dateRequired },
  } = usePaidAndNotPaidForm(orderIds, isSalesPage, onClose);

  const { markerOptions } = usePaidAndNotPaidFormOptions();
  return (
    <FormProvider {...methods}>
      <p dangerouslySetInnerHTML={{ __html: TextForPayedAndNotPaid }} />
      <form className={styles.form} onSubmit={submit}>
        <Label text={PaidAndNotPaidForm.SelectPaymentStatus} className={styles.formLabel} />
        <FormSelectInput<FormData>
          name={'orderMarker'}
          options={markerOptions}
          register={methods.register}
          needClearButton={false}
          error={methods.formState.errors}
          rules={required}
        />
        {needShowDate && (
          <>
            <Label text={PaidAndNotPaidForm.PaymentDate} className={styles.formLabel} />

            <FormDateInput<FormData>
              name={'paymentDate'}
              setValue={methods.setValue}
              needTimePicker={true}
              register={methods.register}
              className={styles.formControl}
              error={methods.formState.errors}
              rules={dateRequired}
            />
            <Label text={PaidAndNotPaidForm.OverwriteDates} className={styles.formLabel}></Label>
            <FormCheckbox<FormData>
              register={methods.register}
              setValue={methods.setValue}
              name={'overwritePreviousDates'}
              error={methods.formState.errors}
              className={styles.formControl}
            />
          </>
        )}

        <ButtonsWrapper>
          <Button
            autoFocus={true}
            type={'submit'}
            theme={BUTTON_THEMES.SUCCESS}
            text={buttons.Update}
            onClick={submit}
          />
        </ButtonsWrapper>
      </form>
    </FormProvider>
  );
}

function SingleNotPaidBody({ onClose, orderIds, isSalesPage, openAmount }: BodyProps & { openAmount: number }) {
  type FormData = ChangeOrderPaymentStatusToPaidForSingleOrderRequest;
  const {
    buttons,
    modalWindows: {
      changePaymentStatus: {
        TextForSingleNotPaid,
        SingleNotPaidOrderForm: { Amount, PaymentDate },
      },
    },
  } = useLocalization();

  const {
    methods,
    rules: { required, shouldBeCurrency, shouldBeMoreThan },
    submit,
  } = useSingleNotPaidForm(orderIds[0], isSalesPage, onClose, openAmount);

  return (
    <FormProvider {...methods}>
      <p dangerouslySetInnerHTML={{ __html: TextForSingleNotPaid }} />

      <form className={styles.form} onSubmit={submit}>
        <Label text={PaymentDate} />
        <FormDateInput<FormData>
          name={'paymentDate'}
          setValue={methods.setValue}
          needTimePicker={true}
          register={methods.register}
          className={styles.formControl}
          rules={required}
          error={methods.formState.errors}
        />
        <Label text={Amount} />
        <NumberFormInput<FormData>
          register={methods.register}
          rules={{ ...shouldBeCurrency, ...required, ...shouldBeMoreThan }}
          setValue={methods.setValue}
          error={methods.formState.errors}
          type="currency"
          name={'amount'}
          className={styles.formControl}
        />

        <ButtonsWrapper>
          <Button theme={BUTTON_THEMES.SUCCESS_TRANSPARENT} text={buttons.Cancel} onClick={onClose} />
          <Button
            autoFocus={true}
            type={'submit'}
            theme={BUTTON_THEMES.SUCCESS}
            text={buttons.Change}
            onClick={submit}
          />
        </ButtonsWrapper>
      </form>
    </FormProvider>
  );
}
